import { useState, useEffect } from "react";
import { X, CheckCircle2, Clock, BookOpen } from "lucide-react";
import apiFetch from "../apiFetch";
import SessionReviewPanel from "./SessionReviewPanel";
import "../styles/homeworkReportModal.css";

const NEEDS_TARGET_COUNT = ["synonyms_game", "antonyms_game", "vocab_matching"];

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function HomeworkReportModal({ assignmentId, role = "teacher", onClose }) {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(false);
  const [reviewSession, setReviewSession] = useState(null); // { completion, num }

  useEffect(() => {
    if (!assignmentId) return;
    setLoading(true);
    setError(false);
    setData(null);
    setReviewSession(null);
    const endpoint = role === "teacher"
      ? `/api/teacher/assignments/${assignmentId}/report`
      : `/api/student/homework/${assignmentId}/report`;
    apiFetch(endpoint)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [assignmentId, role]);

  if (!assignmentId) return null;

  const hw     = data?.assignment;
  const isElo  = hw && NEEDS_TARGET_COUNT.includes(hw.game_type);
  const pct    = hw ? Math.min(100, Math.round((hw.completed_count / Math.max(1, hw.total_count)) * 100)) : 0;
  const isDone = hw && hw.completed_count >= hw.total_count;

  return (
    <>
      <div className="hrm-backdrop" onClick={onClose}>
        <div className="hrm-modal" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="hrm-header">
            <h2 className="hrm-title">Assignment Report</h2>
            <button className="hrm-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
          </div>

          {loading && <div className="hrm-loading">Loading…</div>}
          {error   && <div className="hrm-loading">Failed to load report.</div>}

          {data && (<>
            {/* Assignment info */}
            <div className="hrm-info">
              <p className="hrm-hw-title">{hw.title}</p>
              <div className="hrm-meta">
                {role === "teacher" && hw.student_name && <span>Student: <strong>{hw.student_name}</strong></span>}
                {role === "student" && hw.teacher_name && <span>Set by: <strong>{hw.teacher_name}</strong></span>}
                <span>Assigned: {fmtDate(hw.assigned_at)}</span>
                {hw.due_date && <span>Due: {fmtDate(hw.due_date)}</span>}
              </div>
            </div>

            {/* Progress */}
            <div className="hrm-progress-section">
              <div className="hrm-progress-row">
                <span className="hrm-progress-label">
                  {isElo
                    ? `${hw.completed_count} / ${hw.total_count} correct answers`
                    : `${hw.completed_count} / ${hw.total_count} ${hw.total_count === 1 ? "session" : "sessions"}`}
                </span>
                <span className={`hrm-status-badge ${isDone ? "hrm-badge--done" : "hrm-badge--prog"}`}>
                  {isDone
                    ? <><CheckCircle2 size={13} /> Complete</>
                    : <><Clock size={13} /> In progress</>}
                </span>
              </div>
              <div className="hrm-bar-track">
                <div className="hrm-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="hrm-pct-label">{pct}%</span>
            </div>

            {/* Sessions */}
            <div className="hrm-sessions">
              <p className="hrm-sessions-heading">
                {data.completions.length === 0
                  ? "No sessions recorded"
                  : `${data.completions.length} session${data.completions.length !== 1 ? "s" : ""}`}
              </p>

              {data.completions.length > 0 && (
                <ul className="hrm-session-list">
                  {data.completions.map((c, i) => {
                    const hasScore  = c.score != null && c.max_score > 0;
                    const sessPct   = hasScore ? Math.round((c.score / c.max_score) * 100) : null;
                    const canReview = c.result_json != null;
                    return (
                      <li key={c.id} className="hrm-session-row">
                        <span className="hrm-session-num">#{i + 1}</span>
                        <span className="hrm-session-date">{fmtDate(c.completed_at)}</span>
                        {hasScore ? (
                          <span className="hrm-session-score">
                            {c.score}/{c.max_score}
                            <span className={`hrm-pct-chip ${sessPct >= 80 ? "hrm-chip--green" : sessPct >= 60 ? "hrm-chip--amber" : "hrm-chip--red"}`}>
                              {sessPct}%
                            </span>
                          </span>
                        ) : (
                          <span className="hrm-session-score hrm-session-no-score">—</span>
                        )}
                        {canReview && (
                          <button
                            className="hrm-review-btn"
                            onClick={() => setReviewSession({ completion: c, num: i + 1 })}
                            title="View full question review"
                          >
                            <BookOpen size={13} /> Review
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>)}
        </div>
      </div>

      {/* Full session review overlay */}
      {reviewSession && (
        <SessionReviewPanel
          completion={reviewSession.completion}
          gameType={hw?.game_type}
          sessionNum={reviewSession.num}
          onClose={() => setReviewSession(null)}
        />
      )}
    </>
  );
}
